import { Box, ButtonBase, Container, styled } from '@mui/material';
import { useState } from 'react';

import { routes } from '@/assets/constants/routes-no.ts';
import { CompetitionWinnersDialog } from '@/components/CompetitionWinnersDialog/index.ts';
import { LoanExampleText } from '@/components/LoanExampleText/index.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import type { CompetitionWinner, CompetitionWithConfig } from '@/lib/api/requests.ts';
import { NumberCircle } from '@/ui/NumberCircle.tsx';
import { NumberedList, NumberedListItem } from '@/ui/NumberedList.tsx';

import MountainDesktop from '../_assets/mountain-desktop.svg?styled';
import MountainMobile from '../_assets/mountain-mobile.svg?styled';
import MountainsAndSnow from '../_assets/mountains-and-snow.svg?styled';
import Skis from '../_assets/skis.svg?styled';
import SunCloud from '../_assets/sun-cloud.svg?styled';
import { Board } from '../_components/Board.tsx';
import { CTA } from '../_components/CTA.tsx';
import { useHasEnded } from '../_components/useHasEnded.ts';

const Root = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateAreas: `
    "main"
    "below"
    "disclaimer"
  `,
  width: '100%',
  overflowX: 'clip',
  '--disclaimer-height': '100px',
  gridTemplateRows:
    'minmax(var(--min-height), min(calc(100vh - var(--top-bar-height) - var(--disclaimer-height)), var(--max-height))) auto auto',
  isolation: 'isolate',

  '--min-height': '580px',
  '--max-height': '630px',
  [theme.breakpoints.up('sm')]: {
    '--min-height': '700px',
    '--max-height': '800px',
  },
  [theme.breakpoints.up('lg')]: {
    '--min-height': '800px',
    '--max-height': '1000px',
  },
  [theme.breakpoints.up('xl')]: {
    '--min-height': '900px',
    '--max-height': '1000px',
  },
}));

export const TopSection = (props: {
  activeCompetition: CompetitionWithConfig | undefined;
  winners: CompetitionWinner[] | undefined;
}) => {
  return (
    <Root>
      <Background />
      <Disclaimer />

      <Container
        maxWidth="xl"
        sx={{
          display: 'grid',
          pt: { xs: 4, md: 8 },
          gridArea: 'main / main / below / below',
          gridTemplateRows: 'subgrid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            lg: 'minmax(0, 1fr) auto',
          },
          gap: 6,
        }}
      >
        <Content activeCompetition={props.activeCompetition} winners={props.winners} />
        <Box
          sx={{
            display: 'grid',
            gridArea: { xs: 'below', lg: 'auto' },
            // Workaround for safari
            gridColumn: { xs: '1', lg: 'auto' },
          }}
        >
          <Board />
        </Box>
      </Container>
    </Root>
  );
};

const Background = () => (
  <div className="relative isolate -z-10 bg-[#BAE3FF]" style={{ gridArea: 'main' }}>
    <MountainsAndSnow
      sx={{
        position: 'absolute',
        left: { xs: '-40%', md: 0 },
        right: 0,
        bottom: { xs: -90 },
        height: { xs: '220px', sm: '280px', md: '320px', xl: 'auto' },
        width: { xs: '150%', md: '120%', lg: '100%' },
      }}
    />
    <MountainMobile
      sx={{
        display: { xs: 'block', sm: 'none' },
        position: 'absolute',
        bottom: { xs: '-42px', sm: '-24px' },
        left: 0,
        right: 0,
      }}
    />
    <MountainDesktop
      className="min-[2100px]:hidden!"
      sx={{
        display: { xs: 'none', sm: 'block' },
        position: 'absolute',
        bottom: { xs: '-42px', sm: '-24px', lg: '24px', xl: '56px' },
        left: { sm: '5%', lg: '8%', xl: '15%' },
        // scale: { xl: 1.2 },
      }}
    />
    <SunCloud
      sx={{
        position: 'absolute',
        display: { xs: 'none', sm: 'block' },
        right: { xs: '8px', md: '20%', lg: '35%', xl: '35%' },
        top: { xs: '100px', md: '60px', xl: '12%' },
        // scale: { xl: 1.2 },
      }}
    />
    <Skis
      sx={{
        position: 'absolute',
        display: { xs: 'none', sm: 'block' },
        bottom: { xs: '-42px', sm: '-24px', lg: '12px', xl: '48px' },
        left: { sm: '40%', md: '30%', lg: '20%', xl: '35%' },
        // scale: { xl: 1.2 },
      }}
    />
  </div>
);

const Disclaimer = () => (
  <div className="bg-white" style={{ gridArea: 'disclaimer' }}>
    <Container maxWidth="md">
      <div className="text-center typography-v1-body4 text-balance">
        <LoanExampleText />
      </div>
    </Container>
  </div>
);

const Content = (props: {
  activeCompetition: CompetitionWithConfig | undefined;
  winners: CompetitionWinner[] | undefined;
}) => {
  const ended = useHasEnded();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Box
      sx={{
        maxWidth: { xs: 370, sm: 600, md: 750, xl: 800 },
        mx: { xs: 'auto', sm: 0 },
      }}
    >
      <h1 className="mb-6 text-2xl font-bold text-balance sm:text-5xl lg:mb-8 xl:text-6xl 2xl:text-7xl 2xl:leading-20">
        Er det du som blir <span className="font-extrabold">gjeldfri</span> 30. april?
      </h1>
      <p className="mb-6 typography-v1-h4 md:mb-8">
        Én heldig vinner får slettet inntil 500 000 kr av sin eksisterende kreditt/forbruksgjeld
      </p>
      <p className="mb-2 typography-v1-h4">For å delta:</p>

      <NumberedList
        sx={{ mb: 2 }}
        renderNumber={(props) => (
          <NumberCircle
            sx={{
              fontSize: 14,
              height: 20,
              backgroundColor: '#FBFDFF',
              color: '#031839',
            }}
            {...props}
          />
        )}
        renderLabel={(props) => <p {...props} className="typography-v1-body3" />}
      >
        <NumberedListItem>Logg inn med BankID</NumberedListItem>
        <NumberedListItem>Sjekk om du kan få lavere rente på din gjeld</NumberedListItem>
        <NumberedListItem>Delta i trekningen</NumberedListItem>
      </NumberedList>

      {ended && (
        <p className="mb-6 typography-v1-h4">
          <span>Denne trekningen er avsluttet. </span>
          <ButtonBase
            onClick={() => setIsOpen(true)}
            sx={{
              color: '#74C6FD',
              textDecoration: 'underline',
              mb: 0.5,
              fontWeight: 700,
              ml: 1,
            }}
          >
            Se vinneren her
          </ButtonBase>
        </p>
      )}

      <Box sx={{ mb: 2 }}>
        <a
          href={routes.MARCH_2025_COMPETITION_TERMS}
          style={{ color: 'inherit' }}
          className="underline"
        >
          <span className="typography-v1-body3">Vilkår og betingelser for deltagelse</span>
        </a>
      </Box>

      <CTA
        activeCompetition={props.activeCompetition}
        onClick={() => {
          sendUserPortalEvent('mar25_competition_cta_top_clicked');
        }}
      />

      {props.winners && (
        <CompetitionWinnersDialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          winners={props.winners}
        />
      )}
    </Box>
  );
};
